/**
 * Converts HTML list markup (as stored in dispatch.chiefComplaint/goal caption.en)
 * to a TextMesh Pro-compatible plain text string.
 *
 * List type at each nesting depth (mirrors HtmlEditor CSS):
 *   ol depth 0 → decimal   (1. 2. 3.)
 *   ol depth 1 → alpha     (a. b. c.)
 *   ol depth 2 → roman     (i. ii. iii.)
 *   ul any depth → bullet  (•)
 *
 * Indentation: one \t per nesting level, followed by the prefix, another \t, then text.
 * Example: "\t\ti.\tSub-sub-list item\n"
 *
 * Inline formatting is preserved using TextMeshPro-compatible tags:
 *   <strong>/<b> → <b>, <em>/<i> → <i>, <u> → <u>
 *
 * If the input contains no HTML tags it is returned as-is (plain text passthrough
 * for backward-compatible content saved before this feature).
 *
 * Uses a string tokenizer (no DOM) so this module runs in both browser and Node.js.
 */

const ROMAN_VALS = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
const ROMAN_SYMS = ['m','cm','d','cd','c','xc','l','xl','x','ix','v','iv','i'];

function toRoman(n) {
  let result = '';
  for (let i = 0; i < ROMAN_VALS.length; i++) {
    while (n >= ROMAN_VALS[i]) { result += ROMAN_SYMS[i]; n -= ROMAN_VALS[i]; }
  }
  return result;
}

function toAlpha(n) {
  return String.fromCharCode(96 + n); // 1→'a', 2→'b', …
}

function getPrefix(listType, index, depth) {
  if (listType === 'ul') return '•';
  const cycle = depth % 3;
  if (cycle === 0) return index + '.';
  if (cycle === 1) return toAlpha(index) + '.';
  return toRoman(index) + '.';
}

// Maps HTML inline tags to TextMeshPro-compatible equivalents.
const INLINE_TAG_MAP = { strong: 'b', b: 'b', em: 'i', i: 'i', u: 'u' };

// Renders an inline node, preserving supported formatting tags.
function renderInlineNode(node) {
  if (node.type === 'text') return node.value;
  const inner = node.children.map(renderInlineNode).join('');
  const mappedTag = INLINE_TAG_MAP[node.tag];
  if (mappedTag) return `<${mappedTag}>${inner}</${mappedTag}>`;
  return inner;
}

// Tokenize HTML into open/close/text tokens — works in Node.js and browser.
function tokenize(html) {
  const tokens = [];
  const re = /<(\/?)([a-z][a-z0-9]*)[^>]*>|([^<]+)/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    if (m[2]) {
      tokens.push({ type: m[1] ? 'close' : 'open', tag: m[2].toLowerCase() });
    } else if (m[3] && m[3].trim()) {
      tokens.push({ type: 'text', value: m[3] });
    }
  }
  return tokens;
}

// Recursive descent parser — returns { children, pos }
function parseTokens(tokens, pos) {
  const children = [];
  while (pos < tokens.length) {
    const tok = tokens[pos];
    if (tok.type === 'close') {
      return { children, pos: pos + 1 };
    } else if (tok.type === 'text') {
      children.push({ type: 'text', value: tok.value });
      pos++;
    } else {
      const result = parseTokens(tokens, pos + 1);
      children.push({ type: 'element', tag: tok.tag, children: result.children });
      pos = result.pos;
    }
  }
  return { children, pos };
}

function renderListNode(node, depth) {
  const listType = node.tag; // 'ul' or 'ol'
  let result = '';
  let itemIndex = 0;

  for (const child of node.children) {
    if (child.type !== 'element' || child.tag !== 'li') continue;

    itemIndex++;
    const indent = '\t'.repeat(depth);
    const prefix = getPrefix(listType, itemIndex, depth);

    let itemText = '';
    let nestedResult = '';

    for (const liChild of child.children) {
      if (liChild.tag === 'ul' || liChild.tag === 'ol') {
        nestedResult += renderListNode(liChild, depth + 1);
      } else {
        itemText += renderInlineNode(liChild);
      }
    }

    result += indent + prefix + '\t' + itemText.trim() + '\n';
    result += nestedResult;
  }

  return result;
}

function htmlToTextMeshPro(html) {
  if (!html || !html.trim()) return '';
  if (!html.includes('<')) return html;

  const tokens = tokenize(html);
  const { children } = parseTokens(tokens, 0);

  let result = '';
  for (const node of children) {
    if (node.type === 'element' && (node.tag === 'ul' || node.tag === 'ol')) {
      result += renderListNode(node, 0);
    } else {
      const text = renderInlineNode(node);
      if (text.trim()) result += text + '\n';
    }
  }

  return result.trim();
}

module.exports = { htmlToTextMeshPro };
